import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Input, Modal, Select } from "semantic-ui-react";
import { toast } from "../..";
import { UserData, UserRole } from "../../actions/authentificationActions";
import { TranslateProps } from "../../i18n/TranslateProps";
import { editUser } from "../../api/users";
import { userAuthorityOptions, userRoleOptions } from "../../store/userReducer";

type Props = {
    fetchUsers: () => Promise<void>;
    open: boolean;
    close: () => void;
    user: UserData;
};

export const EditUserForm = ({ user, open, close, fetchUsers }: Props) => {
    const intl = useIntl();
    const [newUser, setNewUser] = useState<UserData>(user);
    const [loading, setLoading] = useState(false);

    const handleEditUser = async () => {
        setLoading(true);
        try {
            await editUser(newUser);
            toast(intl.formatMessage({ id: "userEdited" }), true);
            fetchUsers();
        } catch {
            toast(intl.formatMessage({ id: "unableToEditUser" }), false);
        } finally {
            setLoading(false);
            close();
        }
    };

    useEffect(() => {
        if (newUser.role === UserRole.OfficeUserViadonau) {
            setNewUser(n => ({ ...n, authority: "AT90" }));
        } else if (newUser.authority === "AT90") {
            setNewUser(n => ({ ...n, authority: "AT01" }));
        }
    }, [newUser.role, newUser.authority]);

    return (
        <Modal
            open={open}
            onClose={() => close()}
            dimmer="blurring"
            size="small"
            closeOnDimmerClick={!loading}
        >
            <Modal.Content className="edit-modal">
                <form className="add-user-form">
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "firstName" })}</label>
                        <Input
                            value={newUser.firstName}
                            onChange={(e: any) =>
                                setNewUser({ ...newUser, firstName: e.target.value })
                            }
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "lastName" })}</label>
                        <Input
                            value={newUser.lastName}
                            onChange={(e: any) =>
                                setNewUser({ ...newUser, lastName: e.target.value })
                            }
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "email" })}</label>
                        <Input
                            value={newUser.username}
                            onChange={(e: any) =>
                                setNewUser({ ...newUser, username: e.target.value })
                            }
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "userRole" })}</label>
                        <Select
                            selectOnBlur={false}
                            options={TranslateProps(userRoleOptions)}
                            value={newUser.role}
                            onChange={(e, data: any) =>
                                setNewUser({ ...newUser, role: data.value })
                            }
                        />
                    </section>
                </form>
                <section className="input-container" style={{ marginTop: "10px" }}>
                    <label>{intl.formatMessage({ id: "authority" })}</label>
                    <Select
                        selectOnBlur={false}
                        options={userAuthorityOptions}
                        disabled={newUser.role === UserRole.OfficeUserViadonau}
                        value={newUser.authority}
                        onChange={(e, data: any) =>
                            setNewUser({ ...newUser, authority: data.value })
                        }
                    />
                </section>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    content={intl.formatMessage({ id: "editUser" })}
                    disabled={
                        loading ||
                        !newUser.firstName ||
                        !newUser.lastName ||
                        !newUser.username ||
                        !newUser.role ||
                        !newUser.authority
                    }
                    loading={loading}
                    primary
                    onClick={handleEditUser}
                    fluid
                />
            </Modal.Actions>
        </Modal>
    );
};
